import { z } from 'zod';
import { zConfigApi, zClientDataApi, zDataApi, zListDataApi, zRouteToLocationDataApi, o_HasTopMembers, o_HasWorklistFunctionality, o_HasPopover, o_HasTitle, o_HasHideableCompanion, o_HasUndoButton, o_HasFlowStatus, o_HasSubheaderDownloadLink, o_HasLookBackPeriod, o_IdentifierProps, o_HasSubheaderErrorText, o_HasFab, o_OptionalIdentifierProps, o_GroupingInfo, o_HasConfigure, o_HasFullHeight, o_HasSummaries, o_HasRangeSelector, } from './confdefnView';
import { CartItemType } from './literals';
import { AppType } from './bindings.types';
export const zExtraPivotParam = z.object({
    text: z.string(),
    dataApi: zDataApi,
});
export const zSubheaderDefnProps = z.object({
    groupBy: z.string().optional(),
    sortBy: z.string().optional(),
    rollup: z.string().optional(),
    pareDown: z.string().optional(),
    countLimit: z.string().optional(),
    breadcrumbs: z.array(z.string()).optional(),
    extraPivotParams: z.record(zExtraPivotParam).optional(),
});
const DefnProps = z.object({
    model: z.string(),
    dataApi: zDataApi.optional(),
    assortmentModel: z.string().optional(),
    subheader: zSubheaderDefnProps,
    view: z.array(z.string()),
});
/** Some finagling to use in ConfigGrid for ExtraPivotParams without exceeding the type limit */
const DefnPropsSubheaderOnly = DefnProps.pick({
    subheader: true,
});
const MultiDefnProps = z.object({
    models: z.array(z.string()),
    assortmentModel: z.string().optional(),
    subheader: zSubheaderDefnProps,
    view: z.array(z.string().nullable()), // to accept null for mass edit defn
});
export const o_BaseDefnsComponentProps = (title) => {
    return Object.assign({ defns: DefnProps }, o_HasTitle(title));
};
export const o_BaseMultiDefnsComponentProps = (title) => {
    return Object.assign({ defns: MultiDefnProps }, o_HasTitle(title));
};
export const BaseDefnsComponentProps = (title) => {
    return z.object(o_BaseDefnsComponentProps(title));
};
export const BaseMultiDefnsComponentProps = (title) => {
    return z.object(o_BaseMultiDefnsComponentProps(title));
};
export const CollectionViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Collection View')), o_IdentifierProps), o_HasPopover), o_HasFab), o_HasSubheaderDownloadLink), o_HasTopMembers));
export const CanvasViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Canvas View')), o_OptionalIdentifierProps), o_HasPopover), o_HasFab), o_HasWorklistFunctionality), o_HasFlowStatus), o_HasSubheaderDownloadLink));
export const SummaryViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Summary View')), o_OptionalIdentifierProps), o_HasPopover), o_HasFab), o_HasWorklistFunctionality), o_HasFlowStatus), o_HasSubheaderDownloadLink), o_HasTopMembers));
export const GridViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Grid View')), o_OptionalIdentifierProps), o_HasPopover), o_HasFab), o_HasWorklistFunctionality), o_HasFlowStatus), o_HasSubheaderDownloadLink), o_HasTopMembers), o_HasHideableCompanion), o_HasUndoButton));
export const ColumnGroupedViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Floorset Comparison')), o_IdentifierProps), o_GroupingInfo), o_HasPopover), o_HasFab), o_HasTopMembers), o_HasWorklistFunctionality), o_HasSubheaderDownloadLink), { floorsetApi: zClientDataApi.optional() }));
export const QuickTrendsComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseMultiDefnsComponentProps('Quick Trends')), o_HasTopMembers), o_HasLookBackPeriod), o_HasFlowStatus), { graphDataApi: zListDataApi, gridDataApi: zListDataApi.optional(), floorsetApi: zClientDataApi.optional() }));
// TODO: config should be setup to be models since there are two.
// TODO: we may want to set this up like ListDataConfig (DataApi) but for FitView since that is what the params.aggBy field is really for.
// GroupingInfo is needed so pareto aggBy isnt hardcoded
export const zParetoAnalysisComponentProps = z.object(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Pareto Analysis')), o_GroupingInfo));
export const zParetoDetailsComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Pareto Details')), o_GroupingInfo), { graphDataApi: zDataApi }));
export const NestedAttributeComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Nested Attribute')), o_HasFlowStatus), o_HasSubheaderErrorText), o_HasConfigure), o_HasFullHeight), o_HasSummaries), o_HasTopMembers), { noTreeColumnDefinition: z.boolean().optional().describe('This property is no longer used and can be removed') }));
export const zEditableOverTimeGridComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseMultiDefnsComponentProps('Flow Sheet')), o_IdentifierProps), o_HasPopover), o_HasFab), o_HasFlowStatus), o_HasTopMembers), o_HasUndoButton), o_HasHideableCompanion), { dataApi: zListDataApi.optional() }));
const o_AssortmentAddDetailProps = {
    level: z.string(),
    cartItemType: CartItemType,
    showSelectAll: z.boolean().optional().default(false),
};
const o_AssortmentAddDetailPropsOptional = {
    level: z.string().optional(),
    cartItemType: CartItemType.optional(),
    showSelectAll: z.boolean().optional().default(false),
};
export const ConfigurableGridApiProps = {
    dataApi: z.discriminatedUnion('isListData', [zClientDataApi, zListDataApi]),
    topAttributesApi: z.discriminatedUnion('isListData', [zClientDataApi, zListDataApi]).optional(),
    configApi: zClientDataApi,
    planningApi: zClientDataApi,
    floorsetApi: zClientDataApi.optional(),
    isStyleColorEdit: z.boolean().optional(),
};
export const zConfigurableGridApiProps = z.object(ConfigurableGridApiProps);
export const zConfigurableGridComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, ConfigurableGridApiProps), { defns: DefnPropsSubheaderOnly.optional(), fabType: z.string().optional() }), o_HasTopMembers), o_HasWorklistFunctionality), o_HasPopover), o_HasTitle('Assortment By Floorset')), o_HasHideableCompanion), o_HasUndoButton), o_HasFlowStatus), o_HasSubheaderDownloadLink), o_HasLookBackPeriod), o_IdentifierProps), o_HasSubheaderErrorText), { topMemberObj: z.record(z.any()).optional(), showPublishText: z.boolean().optional(), publishText: z.string().optional(), publishAttribute: z.string().optional() }), o_AssortmentAddDetailPropsOptional));
export const zAssortmentAddViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Select Items')), o_AssortmentAddDetailProps), o_HasFab), o_HasFlowStatus), o_HasRangeSelector));
export const zAssortmentAddBySearchComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Add Choices By Search')), o_AssortmentAddDetailProps), o_HasFab));
// FIXME: the above ConfigApi structure has different a shape and need to match the one in confdefnView.ts
const OvertimeConfigApi = z.object({
    params: z.object({
        appName: z.union([z.literal(AppType.Assortment), z.literal(AppType.TDAnalysis)]).optional(),
        defnId: z.string(),
        aggBy: z.string(),
        ignoreAncestors: z.boolean().optional(),
    }),
});
export const zNestedOvertimeComponentProps = z.object(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Nested Over Time')), { dataApi: OvertimeConfigApi, companionDataApi: OvertimeConfigApi.optional() }));
export const zAssortmentPublishComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Assortment Publish')), o_IdentifierProps), o_HasSubheaderErrorText), { dataApi: zListDataApi.optional() }));
export const zTargetListComponentProps = z.object({
    type: z.union([z.literal('Product'), z.literal('Location')]),
    view: z.array(z.string()),
});
export const zSizeEligibilityListGridComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('')), o_HasSubheaderDownloadLink), { viewProperty: z.string(), floorsetApi: zConfigApi.optional(), dataApi: zListDataApi, companionApi: zListDataApi.optional() }));
export const WorklistComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Workflow')), o_HasFab), o_HasFlowStatus), o_HasHideableCompanion), { hideTitle: z.boolean().optional().default(true), showLevel: z.boolean().optional(), 
    /**
     * Provided to allow bindings to access the path of the default worklist tab
     * without retrieving the worklist viewdefn when building routes.
     *
     * This needs to match a configured worklist tab's `pathSlot` value in the `defns.view[0]`
     * */
    defaultPathSlot: z.string().optional() }));
export const zHistoryGridComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('List View')), o_OptionalIdentifierProps), o_HasTopMembers), o_HasHideableCompanion), o_HasFab), o_HasFlowStatus));
export const zTopPerformersComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Top Performers')), o_HasFlowStatus), o_HasWorklistFunctionality));
export const ExceptionsSummaryComponentProps = z.object({
    dataApi: zListDataApi,
    configApi: zConfigApi,
});
export const zStyleEditComponentProps = z.object(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Style Details View')), o_HasFab));
export const EnhancedOvertimeComponentProps = z.object(Object.assign(Object.assign({}, o_BaseMultiDefnsComponentProps('')), o_HasTopMembers));
export const zAssortmentCartViewComponentProps = z.object(Object.assign({}, o_BaseDefnsComponentProps('')));
export const zProductivityComponentProps = z.object(Object.assign({}, o_BaseDefnsComponentProps('Productivity')));
export const CategorySummaryComponentProps = z.object(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Quick Snapshot')), o_HasTopMembers));
export const ProductMixComponentProps = z.object(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Product Mix')), o_HasTopMembers));
export const zParameterTogglesComponentProps = z.object(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('')), o_HasFab));
export const zProductDetailsComponentProps = z.object(Object.assign({}, o_BaseMultiDefnsComponentProps('Product Details')));
export const zRouteToLocationComponentProps = z.object({
    dataApi: zRouteToLocationDataApi,
});
export const zMacroMixComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign({}, o_BaseDefnsComponentProps('Macro Mix')), o_HasFlowStatus), o_HasLookBackPeriod), { dataApi: zListDataApi, chartDataApi: zListDataApi }));
const o_SummaryDataApiList = {
    keyFinancialsDataApi: zListDataApi,
    choiceProductivityDataApi: zListDataApi,
    analysisDataApi: zListDataApi,
    productMixAndTrendDataApi: zListDataApi,
    geoTrendDataApi: zListDataApi,
};
export const zSummaryTopdownComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign({ defns: z.object({
        model: z.string(),
        subheader: zSubheaderDefnProps,
        view: z.array(z.string()),
    }) }, o_HasTitle('Summary')), o_HasFlowStatus), o_HasLookBackPeriod), o_SummaryDataApiList));
export const zGeoTrendsComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ dataApi: zListDataApi, graphDataApi: zListDataApi }, o_HasLookBackPeriod), o_HasFlowStatus), o_HasTitle('Geo Trends')), o_HasFlowStatus), o_HasTopMembers), { defns: z.object({
        view: z.array(z.string()),
    }) }));
const zDimensionItem = z.object({
    dimension: z.string(),
    items: z.array(z.string()),
    hierarchy: z.optional(z.string()),
});
const o_ViewParams = {
    viewParams: z.object({
        rows: z.array(zDimensionItem),
        columns: z.array(zDimensionItem),
        title: z.string(),
        relocalizeCurrency: z.boolean().optional().default(false),
        summaryMetrics: z.object({
            metrics: zDimensionItem,
            revisions: zDimensionItem,
        }),
    }),
};
const o_SplitViewParams = {
    viewParams: z.object({
        title: z.string(),
        relocalizeCurrency: z.boolean().optional().default(false),
        summaryMetrics: z.object({
            metrics: zDimensionItem,
            revisions: zDimensionItem,
        }),
        topGrid: z.object({
            rows: z.array(zDimensionItem),
            columns: z.array(zDimensionItem),
        }),
        bottomGrid: z.object({
            rows: z.array(zDimensionItem),
            columns: z.array(zDimensionItem),
        }),
    }),
};
const o_SubheaderBtns = {
    subheader: z.object({ extraBtns: z.array(z.string()).optional() }).optional(),
};
const zSubheaderBtns = z.object(o_SubheaderBtns);
export const zMfpSummaryGridComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, o_HasTitle('Plan')), o_ViewParams), o_SubheaderBtns));
export const zMfpSplitViewComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, o_HasTitle('Split View')), o_SplitViewParams), o_SubheaderBtns));
export const zMfpReviewPlansComponentProps = z.object(Object.assign(Object.assign({}, o_HasTitle('Review Plans')), o_ViewParams));
const SMARTPLAN_BALANCE = z.literal('Balance');
const SMARTPLAN_SUMMARY = z.literal('Summary');
const SMARTPLAN_TARGETS = z.literal('Targets');
const SMARTPLAN_GRID = z.literal('Grid');
const SMARTPLAN_DESCRIPTION = z.literal('Description');
const SMARTPLAN_TOGGLE = z.literal('toggle');
const SMARTPLAN_PERCENT = z.literal('inputPercent');
const SMARTPLAN_SLIDER = z.literal('slider');
const SMARTPLAN_INPUTS = z.union([SMARTPLAN_TOGGLE, SMARTPLAN_PERCENT, SMARTPLAN_SLIDER]);
export const zPivotOptions = z.object({
    rows: z.array(zDimensionItem),
    columns: z.array(zDimensionItem),
    name: z.string().optional(),
    groupBy: z
        .object({
        dimension: z.string(),
        item: z.string(),
    })
        .optional(),
    readOnly: z.boolean(),
});
export const zSmartPlanTarget = z.object({
    /* Basic text label for the input */
    text: z.string(),
    /* Popover text input for the string, HTML not allowed */
    infoText: z.string(),
    /* MetricId tied to the input */
    dataIndex: z.string(),
    /* What editor will be used for the input */
    editor: SMARTPLAN_INPUTS,
});
const SMARTPLAN_COMPONENTS = z.union([
    SMARTPLAN_BALANCE,
    SMARTPLAN_SUMMARY,
    SMARTPLAN_TARGETS,
    SMARTPLAN_GRID,
    SMARTPLAN_DESCRIPTION,
]);
export const zSmartPlanSection = z.object({
    title: z.string(),
    icon: z.string(),
    targets: z.array(zSmartPlanTarget),
});
export const zMfpSmartPlanComponentProps = z.object(Object.assign({ 
    /* Section Title (not implemented yet) */
    title: z.string(), 
    /* Section Description */
    description: z.union([z.string(), z.null()]), 
    /* Component Type */
    component: SMARTPLAN_COMPONENTS, 
    /* Sections, leaves as empty array for non-target component */
    sections: z.union([z.null(), z.array(zSmartPlanSection)]), viewParams: z.union([zPivotOptions, z.null()]) }, o_SubheaderBtns));
export const zSmartPlanPlayload = z.object({
    steps: z.array(zMfpSmartPlanComponentProps),
});
export const zBulkImportComponentProps = z.object(Object.assign(Object.assign({}, o_HasTitle('Bulk Import')), { importId: z.string(), stepper1Label: z.string().optional().default('Download a fresh template:'), stepper2Label: z.string().optional().default('Upload a completed template'), templateType: z.string().optional().default('import') }));
